import Phaser from 'phaser';

/**
 * The Fungi Forest's send-off — the cutscene that plays when King Shroometron
 * falls (spec docs/superpowers/specs/2026-10-27-fungi-forest-finale-design.md
 * §2, "The win").
 *
 * Seven seconds, full screen, on EVERY real win of The Shroombellion: Ninja
 * Hambi sprints through the trees, a crowd of mushrooms drops out of them and
 * gives chase, King Shroometron rises ahead, Ninja Hambi leaps at him in slow
 * motion and strikes — the chasers fade, then the trees and the King fade —
 * and the Level Complete screen follows. The Token of Shroom is claimed in
 * GameScene; nothing in here touches progress.
 *
 * An overlay over the HOST scene like `princeNachoCutscene.ts`, for the same
 * reason: it hands back to a Level Complete overlay that lives inside
 * GameScene. A tap skips to the same hand-off.
 */

const W = 1280;
const H = 720;
/** Above every GameScene layer AND its win overlay (which sits at 50). */
const DEPTH = 500;

const T_CHASE = 1600;
const T_KING = 3200;
const T_LEAP = 4000;
const T_HIT = 5500;
const T_FADE = 6000;
const T_TOTAL = 7000;

/** The ground the runners run along. */
const GROUND_Y = 560;

/** Who chases him — the chapter's own shrooms, dropping out of the trees. */
const CHASERS: readonly { key: string; x: number }[] = [
  { key: 'enemy-redshroom', x: 140 },
  { key: 'enemy-glowshroom', x: 250 },
  { key: 'enemy-zombieshroom', x: 360 },
  { key: 'enemy-poisonshroom', x: 200 },
  { key: 'enemy-skullshroom', x: 310 },
  { key: 'enemy-hynofungi', x: 90 },
];

/**
 * Play the send-off. `done` is called exactly once, when it (or a skip)
 * finishes; the returned function cancels it, for GameScene's shutdown.
 */
export function playShroometronCutscene(scene: Phaser.Scene, done: () => void): () => void {
  const objs: Phaser.GameObjects.GameObject[] = [];
  const timers: Phaser.Time.TimerEvent[] = [];
  let finished = false;

  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    objs.push(o);
    return o;
  };
  const later = (ms: number, fn: () => void) => {
    timers.push(scene.time.delayedCall(ms, fn));
  };
  const has = (key: string) => scene.textures.exists(key);
  const teardown = () => {
    timers.forEach((t) => t.remove());
    objs.forEach((o) => {
      scene.tweens.killTweensOf(o);
      o.destroy();
    });
    objs.length = 0;
  };
  const finish = () => {
    if (finished) return;
    finished = true;
    scene.input.off('pointerdown', skip);
    teardown();
    done();
  };

  // ── The stage: black, then the forest scrolling past ──────────────────────
  const cover = add(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x050805, 1)
      .setDepth(DEPTH)
      .setInteractive()
      .setName('shroometron-cutscene'),
  );
  // The forest, drawn twice side by side and scrolled left so the trees rush
  // past: he is running, the camera keeps up with him.
  const forest: Phaser.GameObjects.Image[] = [];
  if (has('bg-shroombellion')) {
    for (let i = 0; i < 2; i++) {
      forest.push(
        add(
          scene.add
            .image(W / 2 + i * W, H / 2, 'bg-shroombellion')
            .setDisplaySize(W, H)
            .setDepth(DEPTH + 1)
            .setTint(0x9fb89f),
        ),
      );
    }
  }
  let scroll = 0;
  let scrollSpeed = 900; // px/s — "in an intense way"
  let last = scene.time.now;
  const roll = scene.time.addEvent({
    delay: 16,
    loop: true,
    callback: () => {
      const now = scene.time.now;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      scroll = (scroll + scrollSpeed * dt) % W;
      forest.forEach((f, i) => f.setX(W / 2 + i * W - scroll));
    },
  });
  timers.push(roll);
  // Speed lines across the frame while he runs.
  const lines = add(scene.add.graphics().setDepth(DEPTH + 2));
  const streak = scene.time.addEvent({
    delay: 50,
    loop: true,
    callback: () => {
      lines.clear();
      if (scrollSpeed < 200) return;
      for (let i = 0; i < 9; i++) {
        const y = 90 + ((i * 131 + Math.floor(scene.time.now / 50) * 37) % 520);
        const x = (i * 211 + Math.floor(scene.time.now / 50) * 97) % W;
        lines.lineStyle(2, 0xffffff, 0.22);
        lines.lineBetween(x, y, x + 120, y);
      }
    },
  });
  timers.push(streak);

  // ── 0.0s: NINJA HAMBI sprinting through the trees ─────────────────────────
  const ninjaKey = has('tower-ninja-1') ? 'tower-ninja-1' : 'tower-patty-1';
  const ninja = add(
    scene.add
      .image(-120, GROUND_Y, ninjaKey)
      .setDisplaySize(130, 130)
      .setDepth(DEPTH + 5)
      .setName('shroometron-cutscene-ninja'),
  );
  scene.tweens.add({ targets: ninja, x: 560, duration: 900, ease: 'Cubic.easeOut' });
  // A running bob.
  const bob = scene.tweens.add({
    targets: ninja,
    y: GROUND_Y - 16,
    duration: 140,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });

  // ── 1.6s: the mushrooms drop out of the trees and give chase ──────────────
  const chasers: Phaser.GameObjects.Image[] = [];
  later(T_CHASE, () => {
    CHASERS.forEach((c, i) => {
      if (!has(c.key)) return;
      const m = add(
        scene.add
          .image(c.x, -80, c.key)
          .setDisplaySize(84, 84)
          .setDepth(DEPTH + 4)
          .setName('shroometron-cutscene-chaser'),
      );
      chasers.push(m);
      scene.tweens.add({
        targets: m,
        y: GROUND_Y + 10 - (i % 2) * 24,
        duration: 420,
        delay: i * 90,
        ease: 'Bounce.easeOut',
        onComplete: () => {
          // ...and hop after him.
          scene.tweens.add({
            targets: m,
            x: m.x + 140,
            y: m.y - 22,
            duration: 220,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        },
      });
    });
  });

  // ── 3.2s: KING SHROOMETRON rises ahead ────────────────────────────────────
  const kingKey = has('enemy-kingshroometron') ? 'enemy-kingshroometron' : 'enemy-redshroom';
  const king = add(
    scene.add
      .image(1050, H + 200, kingKey)
      .setDisplaySize(340, 340)
      .setDepth(DEPTH + 3)
      .setName('shroometron-cutscene-king'),
  );
  later(T_KING, () => {
    scrollSpeed = 160; // the chase slows as he looms up
    scene.tweens.add({ targets: king, y: GROUND_Y - 120, duration: 700, ease: 'Back.easeOut' });
    scene.cameras.main.shake(400, 0.006);
  });

  // ── 4.0s: the leap, in SLOW MOTION ────────────────────────────────────────
  later(T_LEAP, () => {
    bob.stop();
    scrollSpeed = 40;
    scene.tweens.add({
      targets: ninja,
      x: 960,
      duration: T_HIT - T_LEAP,
      ease: 'Sine.easeInOut',
    });
    scene.tweens.add({
      targets: ninja,
      y: GROUND_Y - 330,
      angle: 360,
      duration: (T_HIT - T_LEAP) * 0.6,
      ease: 'Quad.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: ninja,
          y: GROUND_Y - 160,
          angle: 400,
          duration: (T_HIT - T_LEAP) * 0.4,
          ease: 'Quad.easeIn',
        });
      },
    });
  });

  // ── 5.5s: the strike — the chasers fade away ──────────────────────────────
  later(T_HIT, () => {
    scrollSpeed = 0;
    const flash = add(scene.add.rectangle(W / 2, H / 2, W, H, 0xffffff, 0.85).setDepth(DEPTH + 8));
    scene.tweens.add({ targets: flash, alpha: 0, duration: 420 });
    scene.cameras.main.shake(380, 0.012);
    for (const c of chasers) {
      scene.tweens.killTweensOf(c);
      scene.tweens.add({ targets: c, alpha: 0, duration: 450 });
    }
  });

  // ── 6.0s: the trees and the King fade away ────────────────────────────────
  later(T_FADE, () => {
    scene.tweens.add({ targets: [...forest, king], alpha: 0, duration: 800 });
    scene.tweens.add({ targets: ninja, alpha: 0, duration: 800, delay: 200 });
  });

  // ── 7.0s: hand over to the Level Complete screen ──────────────────────────
  later(T_TOTAL, finish);

  const skip = () => finish();
  cover.on('pointerdown', skip);
  scene.input.on('pointerdown', skip);

  return () => {
    if (finished) return;
    finished = true;
    scene.input.off('pointerdown', skip);
    teardown();
  };
}
